import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { connect, useDispatch, useSelector } from 'react-redux';
import Pagination from 'react-js-pagination';
import { marketingInvoiceAction } from '../../_actions/marketingInvoice.actions';
import './SettingsModern.css';

/* The server can hand back a null amount — a salon whose settings row had no
   freeSmsLimit produced NaN, which JSON turns into null. inv.amount.toFixed(2)
   then throws and takes the whole billing page down with it. A billing screen
   must never be the thing that breaks; show a zero and let the number be
   wrong rather than showing the owner nothing at all. */
function money(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

function Invoices() {
    const dispatch = useDispatch();
    const [invoiceList, setInvoiceList] = useState([]);
    const [isLoader, setIsLoader] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [activePage, setActivePage] = useState(1);
    const itemPerPageLimit = 10;

    useEffect(() => {
        const salonId = localStorage.getItem('salonId');
        dispatch(marketingInvoiceAction?.getInvoiceList({
            salonId,
            currentPage: activePage,
            limit: itemPerPageLimit,
        })).then((res) => {
            if (res?.data?.statusCode === 1) {
                setInvoiceList(res.data.responseData?.result || []);
                setTotalCount(res.data.responseData?.totalCount || 0);
            }
            setIsLoader(false);
        });
        /* eslint-disable-next-line */
    }, [activePage]);

    const formatDate = (date, type) => {
        if (type === 'billing') return moment(date, 'YYYYMMDD').format('MMM YYYY');
        if (type === 'expected') return moment(date).format('D MMM YYYY');
        return date;
    };

    if (isLoader) return null;

    return (
        <div className="settings-card">
            <div className="settings-card-header">
                <h2>Invoices</h2>
                <p>Billing history for Beu platform fees.</p>
            </div>

            {invoiceList?.length > 0 ? (
                <>
                    {/* Desktop table */}
                    <div className="bi-invoice-table-wrap">
                        <table className="settings-table">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Date</th>
                                    <th>SMS</th>
                                    <th>SMS Fees</th>
                                    <th>Card Fees</th>
                                    <th>Total</th>
                                    <th style={{ width: 44 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {invoiceList.map((inv) => (
                                    <tr key={inv._id} style={{ cursor: inv?.stripeInvoicePdf ? 'pointer' : 'default' }}
                                        onClick={() => inv?.stripeInvoicePdf && window.open(inv.stripeInvoicePdf)}>
                                        <td style={{ fontWeight: 500 }}>{formatDate(inv.invoiceMonth, 'billing')}</td>
                                        <td>{formatDate(inv.createDate, 'expected')}</td>
                                        <td>{inv.smsCount}</td>
                                        <td>{inv.currencySymbol}{money(inv.smsFees)}</td>
                                        <td>{inv.currencySymbol}{money(inv.cardCaptureFees)}</td>
                                        <td style={{ fontWeight: 600 }}>{inv.currencySymbol}{money(inv.amount)}</td>
                                        <td>
                                            {inv.stripeInvoicePdf && (
                                                <span className="bi-download-btn">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                                        <polyline points="7 10 12 15 17 10"/>
                                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                                    </svg>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="bi-invoice-mobile">
                        {invoiceList.map((inv) => (
                            <div
                                key={inv._id}
                                className="bi-invoice-card"
                                onClick={() => inv?.stripeInvoicePdf && window.open(inv.stripeInvoicePdf)}
                            >
                                <div className="bi-invoice-card-top">
                                    <span className="bi-invoice-card-period">{formatDate(inv.invoiceMonth, 'billing')}</span>
                                    <span className="bi-invoice-card-total">{inv.currencySymbol}{money(inv.amount)}</span>
                                </div>
                                <div className="bi-invoice-card-bottom">
                                    <span>{formatDate(inv.createDate, 'expected')}</span>
                                    <span>{inv.smsCount} SMS</span>
                                    {inv.stripeInvoicePdf && (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                            <polyline points="7 10 12 15 17 10"/>
                                            <line x1="12" y1="15" x2="12" y2="3"/>
                                        </svg>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalCount >= itemPerPageLimit && (
                        <div className="settings-pagination">
                            <Pagination
                                activePage={activePage}
                                itemsCountPerPage={itemPerPageLimit}
                                totalItemsCount={totalCount}
                                pageRangeDisplayed={3}
                                onChange={(page) => setActivePage(page)}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="bi-empty">
                    <div className="bi-empty-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                    </div>
                    <p>No invoices yet</p>
                    <span>Invoices will appear here once billing begins</span>
                </div>
            )}
        </div>
    );
}

function mapStateToProps(state) {
    return { ...state };
}

export default connect(mapStateToProps)(Invoices);
